import type { UpdateOrderStatusParams } from '@/entities/order/model'
import type { ProofOfAgePayload } from '@/features/order/model'
import { Box, Button, Center, Flex, Modal, Paper, PinInput, Radio, Stack, Text } from '@mantine/core'
import { IconChevronLeft, IconX } from '@tabler/icons-react'
import clsx from 'clsx'
import { useState } from 'react'
import { useOrderStatusMutation, useRawOrdersQuery } from '@/entities/order/model'
import { useStateConfigQuery, useTmInitialsEnabled } from '@/entities/state/model'
import { useOrderGroupMembers } from '@/features/order/lib/useOrderGroupMembers'
import { usePoaFlowStore } from '@/features/order/model'
import { useTmInitialsStore } from '@/features/order/ui/initials-capture-modal'
import { useResponsiveModal } from '@/shared/lib/mantine'
import modalClasses from '@/shared/ui/component-styles/Modal.module.css'
import { HeaderIconButton } from '@/shared/ui/header-icon-button'
import classes from './CollectionCodeModal.module.css'

interface CollectionCodeModalProps {
  opened: boolean
  onClose: () => void
  onSuccess?: () => void
  orderId: string
  /**
   * The orders this completion applies to (BD-8114: a single order's detail
   * modal passes just that order). Falls back to the whole group when omitted.
   */
  orderIds?: string[]
  nextStatus: string
  lastUpdateTime?: string
  /**
   * BD-8761: the Proof of Age record from checks already completed for this order.
   * Submitted on the same status patch as the code or exception reason, so the
   * completion, the verification and the record land together.
   */
  proofOfAge?: ProofOfAgePayload
}

interface CollectionCodeEntryCardProps {
  code: string
  disabled?: boolean
  dataOnboarding?: string
  onChange?: (value: string) => void
}

export function CollectionCodeEntryCard({
  code,
  disabled = false,
  dataOnboarding,
  onChange,
}: CollectionCodeEntryCardProps) {
  const modalConfig = useResponsiveModal()

  return (
    <Paper
      radius="md"
      p="xl"
      bg="white"
      shadow="xs"
      w={modalConfig.isSmUp ? '100%' : undefined}
      data-onboarding={dataOnboarding}
    >
      <Stack gap="xl" align="center">
        <Stack gap="xs" align="center">
          <Text fw={700} size="xl" ta="center">
            Enter verification code
          </Text>
          <Text c="gray.6" size="sm" ta="center">
            Ask the customer for the last 4 digits of their mobile number
          </Text>
        </Stack>

        <PinInput
          length={4}
          type="number"
          autoFocus={!disabled}
          value={code}
          placeholder=""
          onChange={onChange}
          size={modalConfig.isSmUp ? 'lg' : 'xl'}
          disabled={disabled}
          styles={{ input: { fontSize: modalConfig.isSmUp ? '1.5rem' : '1.75rem', fontWeight: 700, ...(modalConfig.isSmUp && { height: '3.5rem' }) } }}
        />
      </Stack>
    </Paper>
  )
}

export function CollectionCodeModal({
  opened,
  onClose,
  onSuccess,
  orderId,
  orderIds,
  nextStatus,
  lastUpdateTime,
  proofOfAge,
}: CollectionCodeModalProps) {
  const [code, setCode] = useState('')
  const [view, setView] = useState<'code' | 'exception'>('code')
  const [selectedExceptionReason, setSelectedExceptionReason] = useState<string | null>(null)
  const modalConfig = useResponsiveModal()
  const { mutateAsync: updateStatus, isPending } = useOrderStatusMutation()
  const { data: stateConfig } = useStateConfigQuery()
  const groupMemberOrderIds = useOrderGroupMembers(orderId)
  const { data: allOrders } = useRawOrdersQuery()
  const isTmInitialsEnabled = useTmInitialsEnabled()
  const enqueueInitials = useTmInitialsStore(state => state.enqueue)
  const clearPoaRecord = usePoaFlowStore(state => state.clearOrder)

  const order = allOrders?.find(o => o.orderId === orderId)
  const customerName = order?.customerName ?? 'Customer'

  // The orders this completion applies to: the opener's explicit scope, or the
  // whole group when opened without one.
  const targetOrderIds = orderIds ?? groupMemberOrderIds

  // Collection code submissions complete the order, so queue the initials
  // capture once for exactly the orders this completion applies to.
  function queueInitialsIfCompleted() {
    if (nextStatus === 'Completed' && isTmInitialsEnabled) {
      enqueueInitials({ orderId, orderIds: targetOrderIds, customerName: order?.customerName })
    }
  }

  // The backend has the Proof of Age record once the order completes, so the local
  // answers are discarded. Only on success: a rejected code keeps them for a retry.
  function clearPoaRecordIfSubmitted() {
    if (proofOfAge) {
      clearPoaRecord(orderId)
    }
  }

  function handleClose() {
    setCode('')
    setView('code')
    setSelectedExceptionReason(null)
    onClose()
  }

  const exceptionReasons = stateConfig?.exceptionReasons
    ?.filter(reason => reason.enabled)
    .map(reason => ({
      value: reason.reason,
      label: reason.reason,
    })) ?? []

  async function handleCodeSubmit() {
    if (code.length !== 4)
      return

    try {
      const updatePromises = targetOrderIds.map((memberId) => {
        const updateParams: UpdateOrderStatusParams = {
          orderId: memberId,
          newStatus: nextStatus,
          lastUpdateTime,
          verificationData: {
            verificationCode: code,
          },
          proofOfAge,
        }
        return updateStatus(updateParams)
      })

      await Promise.all(updatePromises)
      clearPoaRecordIfSubmitted()
      queueInitialsIfCompleted()
      setCode('')
      if (onSuccess) {
        onSuccess()
      } else {
        onClose()
      }
    } catch (error) {
      console.error('Failed to submit collection code:', error)
    }
  }

  async function handleExceptionSubmit() {
    if (!selectedExceptionReason)
      return

    try {
      const updatePromises = targetOrderIds.map((memberId) => {
        const updateParams: UpdateOrderStatusParams = {
          orderId: memberId,
          newStatus: nextStatus,
          lastUpdateTime,
          verificationData: {
            exceptionReason: selectedExceptionReason,
          },
          proofOfAge,
        }
        return updateStatus(updateParams)
      })

      await Promise.all(updatePromises)
      clearPoaRecordIfSubmitted()
      queueInitialsIfCompleted()
      setCode('')
      setView('code')
      setSelectedExceptionReason(null)
      if (onSuccess) {
        onSuccess()
      } else {
        onClose()
      }
    } catch (error) {
      console.error('Failed to submit exception reason:', error)
    }
  }

  function openExceptionReasonsView() {
    setCode('')
    setView('exception')
    setSelectedExceptionReason(null)
  }

  function returnToCodeEntryView() {
    setSelectedExceptionReason(null)
    setView('code')
  }

  const codeEntryCard = (
    <Paper radius="md" p="xl" bg="white" shadow="xs" w={modalConfig.isSmUp ? '100%' : undefined}>
      <Stack gap="xl" align="center">
        <Stack gap="xs" align="center">
          <Text fw={700} size="xl" ta="center">
            Enter verification code
          </Text>
          <Text c="gray.6" size="sm" ta="center">
            Ask the customer for the last 4 digits of their mobile number
          </Text>
        </Stack>

        <PinInput
          length={4}
          type="number"
          autoFocus
          value={code}
          placeholder=""
          onChange={setCode}
          size={modalConfig.isSmUp ? 'lg' : 'xl'}
          disabled={isPending}
          styles={{ input: { fontSize: modalConfig.isSmUp ? '1.5rem' : '1.75rem', fontWeight: 700, ...(modalConfig.isSmUp && { height: '3.5rem' }) } }}
        />
      </Stack>
    </Paper>
  )

  const exceptionReasonsCard = (
    <Paper radius="md" p="xl" bg="white" shadow="xs" w={modalConfig.isSmUp ? '100%' : undefined}>
      <Stack gap="lg">
        <Stack gap={2} align="center">
          <Text fw={700} size="xl" ta="center">
            Why is the code unavailable?
          </Text>
          <Text c="gray.6" size="sm" ta="center">
            Select a reason to continue without verification
          </Text>
        </Stack>

        <Radio.Group
          value={selectedExceptionReason ?? ''}
          onChange={setSelectedExceptionReason}
        >
          {exceptionReasons.length === 0
            ? (
                <Text c="gray.6" size="sm" ta="center">
                  No reason available
                </Text>
              )
            : (
                <Stack gap="sm">
                  {exceptionReasons.map(reason => (
                    <Radio
                      key={reason.value}
                      value={reason.value}
                      label={reason.label}
                      disabled={isPending}
                    />
                  ))}
                </Stack>
              )}
        </Radio.Group>
      </Stack>
    </Paper>
  )

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={null}
      size={modalConfig.size}
      fullScreen={modalConfig.fullScreen}
      transitionProps={modalConfig.transitionProps}
      classNames={{
        content: modalClasses.modalContent,
        body: modalClasses.modalBody,
      }}
    >
      <div
        className={clsx(
          modalClasses.modalHeader,
          !modalConfig.isSmUp
            ? modalClasses.mobileHeader
            : modalClasses.desktopHeader,
        )}
      >
        {!modalConfig.isSmUp ? (
          <>
            <HeaderIconButton
              ariaLabel="Go back"
              onClick={handleClose}
              icon={<IconChevronLeft size={32} />}
            />

            <h3 className={modalClasses.headerTitle}>{customerName}</h3>

            <div />
          </>
        ) : (
          <>
            <h3 className={modalClasses.headerTitle}>{customerName}</h3>

            <HeaderIconButton
              ariaLabel="Close modal"
              onClick={handleClose}
              icon={<IconX size={32} />}
            />
          </>
        )}
      </div>

      <Box className={modalClasses.content}>
        {modalConfig.isSmUp
          ? (
              <Center h="100%">
                {view === 'code' ? codeEntryCard : exceptionReasonsCard}
              </Center>
            )
          : (
              view === 'code' ? codeEntryCard : exceptionReasonsCard
            )}
      </Box>

      <Box className={classes.actions}>
        <Flex
          direction={modalConfig.isSmUp ? 'row' : 'column'}
          justify={modalConfig.isSmUp ? 'space-between' : undefined}
          align={modalConfig.isSmUp ? 'center' : 'stretch'}
          w="100%"
          gap="xs"
        >
          {view === 'code'
            ? (
                <>
                  <Button
                    variant="transparent"
                    color="gray.6"
                    size="md"
                    p={0}
                    onClick={openExceptionReasonsView}
                    disabled={isPending}
                  >
                    Can&apos;t provide code?
                  </Button>

                  <Button
                    variant="filled"
                    color="gray.8"
                    size={modalConfig.isSmUp ? 'md' : 'lg'}
                    radius="md"
                    fullWidth={!modalConfig.isSmUp}
                    disabled={code.length !== 4 || isPending}
                    loading={isPending}
                    onClick={handleCodeSubmit}
                  >
                    Complete order
                  </Button>
                </>
              )
            : (
                <>
                  <Button
                    variant="transparent"
                    color="gray.8"
                    size="md"
                    p={0}
                    onClick={returnToCodeEntryView}
                    disabled={isPending}
                  >
                    Back to Code Entry
                  </Button>

                  <Button
                    variant="filled"
                    color="gray.8"
                    size={modalConfig.isSmUp ? 'md' : 'lg'}
                    radius="md"
                    fullWidth={!modalConfig.isSmUp}
                    disabled={!selectedExceptionReason || isPending}
                    loading={isPending}
                    onClick={handleExceptionSubmit}
                  >
                    Submit and Complete Order
                  </Button>
                </>
              )}
        </Flex>
      </Box>
    </Modal>
  )
}
